/**
 * The ten scenes.
 *
 * A scene is a skin: the same Pugo, the same layout, the same components in
 * the same places - wearing its own colours, glass, borders, buttons, glow
 * and weather. Everything a scene decides lives in this one file, and no
 * page, view or component knows a scene by name.
 *
 * styles/scenes.css and the boot script in index.html are generated from the
 * table below, so the three cannot drift apart:
 *
 *   node tools/themes/build-scenes.mjs
 *
 * An eleventh scene is a new entry here, two pictures through
 * tools/themes/import-assets.mjs, one line in server/catalog.js, and that
 * command.
 *
 * Kept free of imports on purpose: the build tool reads this file directly
 * with node type stripping.
 */

/** Every scene id, in the order the picker shows them. */
export const SCENE_IDS = [
  'sakura-night',
  'jade-aurora',
  'frontier-outlaw',
  'lavender-nebula',
  'pixel-realm',
  'dream-portal',
  'void-forge',
  'matrix-lime',
  'jungle-temple',
  'ruby-midnight'
] as const;

export type SceneId = (typeof SCENE_IDS)[number];

/**
 * What drifts past behind the panels. One engine draws all of them -
 * components/Ambience.tsx - so a new scene picks a type rather than
 * bringing its own animation.
 */
export type ParticleType =
  | 'petal' | 'mist' | 'dust' | 'star' | 'pixel'
  | 'crystal' | 'ember' | 'digital' | 'firefly' | 'cosmic';

/**
 * The character of a border.
 *
 * Not a colour - a treatment. A panel in Frontier Outlaw has brass corner
 * rivets, one in Pixel Realm has stepped corners, one in Void Forge has a
 * lava edge along the bottom. Drawn from the palette by the build tool.
 */
export type EdgeKind =
  | 'blossom' | 'aurora' | 'rivet' | 'halo' | 'pixel'
  | 'crystal' | 'lava' | 'scan' | 'gold' | 'ruby';

/** How a button is made. Same size, same padding, different material. */
export type ButtonKind = 'glass' | 'gradient' | 'leather' | 'block' | 'forge' | 'tech' | 'stone';

/** What marks the page you are on in the sidebar. */
export type MarkKind = 'glow' | 'brass' | 'halo' | 'square' | 'lava' | 'line' | 'goldline';

/**
 * How a panel is framed.
 *
 * Measured off the references: a card in Frontier Outlaw is not a rectangle
 * with a border, it is a plank with a dark groove cut into it and a brass
 * line inside that. Jungle Temple is the same idea in stone and gold, Void
 * Forge in blackened iron. The rest are a single hairline, which is what
 * they are in their own references.
 */
export type FrameKind = 'hair' | 'plank' | 'stone' | 'forged' | 'step' | 'edge';

/**
 * The face a scene's headings are set in. Headings only - a western font on
 * the word "Message" is a costume rather than a theme.
 */
export type DisplayKind = 'app' | 'slab' | 'serif' | 'tech';

/** The palette. Every value is a colour; nothing here is a measurement. */
export type SceneColors = {
  bg: string;
  bgSecondary: string;
  surface: string;
  surfaceHover: string;
  surfaceElevated: string;
  surfaceStrong: string;
  border: string;
  borderHover: string;
  borderBright: string;
  primary: string;
  primaryHover: string;
  primaryPressed: string;
  primarySoft: string;
  primaryForeground: string;
  accent: string;
  accentSecondary: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  success: string;
  warning: string;
  danger: string;
  offline: string;
  glow: string;
  glowStrong: string;
  glass: string;
};

/** How the scene is lit, and what moves in it. */
export type SceneEffects = {
  /** How far the glass blurs what is behind it. */
  panelBlur: string;
  /** A little lift behind the glass, not a filter. */
  panelSaturation: string;
  /** How solid a normal card is over the wallpaper, 0-1. */
  panelOpacity: number;
  wallpaperBrightness: number;
  wallpaperSaturation: number;
  /** A flat wash over the wallpaper, under the UI. */
  wallpaperOverlay: string;
  ambientParticleType: ParticleType;
  /** How many are visible at once in a desktop window. */
  ambientParticleAmount: number;
  /** A multiplier on the type's own speed. */
  ambientParticleSpeed: number;
  /** How far the scene glow carries, 0-1. */
  ambientGlowStrength: number;
  /** A soft light behind the wallpaper - a moon, a portal, lava. */
  bloom?: string;
  /** Whether that light breathes. Reduced motion stops it. */
  bloomPulse?: boolean;
};

/**
 * The skin.
 *
 * Sizes, spacing and positions are not in here and never will be: a scene
 * changes what things are made of, not where they are. The one measurement
 * is the corner radius, which the brief allows to differ - Matrix Lime is
 * nearly square, Dream Portal is soft - and which rides on the same
 * multiplier as the Corner Style setting, so choosing one still wins.
 */
/**
 * The material each surface is made of.
 *
 * Measured off the ten reference pictures by tools/themes/sample.mjs rather
 * than chosen: how solid a panel is over its wallpaper is most of what makes
 * one scene feel different from another, and it is the one thing that cannot
 * be guessed. Dream Portal's panel is a lavender at 66%; Void Forge's is
 * near-black at 90%. Every value is an rgba() because every one of them has
 * a photograph behind it.
 */
export type SceneSurfaces = {
  /** The main panel and the cards on it. */
  panel: string;
  /** A row inside a panel. */
  row: string;
  /** The cards down the right. */
  card: string;
  sidebar: string;
  /** The bar across the top - the most transparent thing in the app. */
  nav: string;
  /** Inputs and wells. */
  input: string;
  /** The plate behind the page you are on. */
  selected: string;
};

export type SceneStyle = {
  /**
   * The whole radius ladder, scaled.
   *
   * Pogu's own ladder is 6/8/10/12/14/16/18/22; a scene multiplies it. The
   * numbers are the brief's per-theme table divided by that ladder, so
   * Frontier Outlaw's "panels 8-10, buttons 5-7" comes out at 0.5 and Dream
   * Portal's "panels 16-18" at 0.95. No dimension changes, only corners.
   */
  radius: number;
  /**
   * How far this scene's glass blurs. Zero for the ones the brief calls
   * material rather than glass - leather, obsidian, stone - where a blur
   * would read as a smudge.
   */
  blur: string;
  /** Menus, dropdowns and modals - the readable end of the range. */
  modalOpacity: number;
  edge: EdgeKind;
  button: ButtonKind;
  mark: MarkKind;
  frame: FrameKind;
  display: DisplayKind;
  /**
   * How present a border is, 0-1.
   *
   * The western and stone scenes draw a line round everything - every chip,
   * every row, every button - and the glass ones barely draw one at all.
   * This is the alpha of the quiet border; the loud one follows it.
   */
  line: number;
};

export type Scene = {
  id: SceneId;
  name: string;
  /** One line, shown under the name in Settings. */
  blurb: string;
  /** The wallpaper at card size, drawn on the picker card. */
  thumbnail: string;
  /** The clean wallpaper, drawn behind the whole app. */
  backgroundImage: string;
  backgroundPosition: string;
  /** A touch over 1, so the edges of the photograph never show. */
  backgroundScale: number;
  colors: SceneColors;
  effects: SceneEffects;
  surfaces: SceneSurfaces;
  style: SceneStyle;
};

export const SCENES: readonly Scene[] = [
  {
    id: 'sakura-night',
    name: 'Sakura Night',
    blurb: 'A romantic Japanese night by the lake.',
    thumbnail: '/themes/01-sakura-night-thumb.webp',
    backgroundImage: '/themes/01-sakura-night-background.webp',
    backgroundPosition: 'center center',
    backgroundScale: 1.02,
    colors: {
      bg: '#100912',
      bgSecondary: '#180D1C',
      surface: '#211222',
      surfaceHover: '#2D182E',
      surfaceElevated: '#361D36',
      surfaceStrong: '#170C18',
      border: '#553047',
      borderHover: '#86415F',
      borderBright: '#D54D8A',
      primary: '#F05291',
      primaryHover: '#FF70A9',
      primaryPressed: '#D83C78',
      primarySoft: '#4B1D35',
      primaryForeground: '#FFFFFF',
      accent: '#FF9BC4',
      accentSecondary: '#BE74FF',
      text: '#FFF6FA',
      textSecondary: '#D2B4C5',
      textMuted: '#8C7281',
      success: '#39D98A',
      warning: '#FFC857',
      danger: '#FF5263',
      offline: '#7B7180',
      glow: 'rgba(240,82,145,.32)',
      glowStrong: 'rgba(255,112,169,.52)',
      glass: 'rgba(24,11,27,.79)'
    },
    effects: {
      panelBlur: '18px',
      panelSaturation: '125%',
      panelOpacity: 0.79,
      wallpaperBrightness: 0.79,
      wallpaperSaturation: 1.08,
      wallpaperOverlay: 'rgba(12,5,14,.27)',
      ambientParticleType: 'petal',
      ambientParticleAmount: 16,
      ambientParticleSpeed: 0.55,
      ambientGlowStrength: 0.9,
      bloom: 'radial-gradient(60% 45% at 50% 18%, rgba(255,150,195,.20), transparent 70%)'
    },
    surfaces: {
      panel: 'rgba(20,17,24,.86)',
      row: 'rgba(30,25,33,.80)',
      card: 'rgba(26,20,28,.84)',
      sidebar: 'rgba(24,18,25,.83)',
      nav: 'rgba(12,11,18,.80)',
      input: 'rgba(23,22,28,.88)',
      selected: 'rgba(58,30,46,.78)'
    },
    style: {
      radius: 0.88,
      blur: '14px',
      modalOpacity: 0.92,
      edge: 'blossom',
      button: 'glass',
      mark: 'glow',
      frame: 'hair',
      display: 'app',
      line: 0.5    }
  },
  {
    id: 'jade-aurora',
    name: 'Jade Aurora',
    blurb: 'Cold mountains under an emerald aurora.',
    thumbnail: '/themes/02-jade-aurora-thumb.webp',
    backgroundImage: '/themes/02-jade-aurora-background.webp',
    backgroundPosition: 'center center',
    backgroundScale: 1.02,
    colors: {
      bg: '#041417',
      bgSecondary: '#071F22',
      surface: '#09292B',
      surfaceHover: '#0E3838',
      surfaceElevated: '#124443',
      surfaceStrong: '#061C1F',
      border: '#235A55',
      borderHover: '#318577',
      borderBright: '#41D8AD',
      primary: '#2BD7A0',
      primaryHover: '#4BE8B7',
      primaryPressed: '#20BA88',
      primarySoft: '#0D483B',
      primaryForeground: '#04120E',
      accent: '#6DF7CF',
      accentSecondary: '#4EB7D4',
      text: '#F2FFFB',
      textSecondary: '#AAD0C8',
      textMuted: '#668D86',
      success: '#31DB94',
      warning: '#F6CB58',
      danger: '#FF5C68',
      offline: '#66807C',
      glow: 'rgba(43,215,160,.30)',
      glowStrong: 'rgba(75,232,183,.50)',
      glass: 'rgba(4,28,30,.80)'
    },
    effects: {
      panelBlur: '18px',
      panelSaturation: '128%',
      panelOpacity: 0.8,
      wallpaperBrightness: 0.78,
      wallpaperSaturation: 1.10,
      wallpaperOverlay: 'rgba(3,19,21,.23)',
      ambientParticleType: 'mist',
      ambientParticleAmount: 12,
      ambientParticleSpeed: 0.3,
      ambientGlowStrength: 0.85,
      bloom: 'radial-gradient(70% 50% at 50% 12%, rgba(77,231,183,.16), transparent 72%)',
      bloomPulse: true
    },
    surfaces: {
      panel: 'rgba(16,44,48,.70)',
      row: 'rgba(8,29,33,.84)',
      card: 'rgba(13,35,40,.72)',
      sidebar: 'rgba(4,28,32,.88)',
      nav: 'rgba(4,20,26,.66)',
      input: 'rgba(10,31,36,.90)',
      selected: 'rgba(15,72,58,.70)'
    },
    style: {
      radius: 0.88,
      blur: '12px',
      modalOpacity: 0.92,
      edge: 'aurora',
      button: 'glass',
      mark: 'glow',
      frame: 'hair',
      display: 'app',
      line: 0.52    }
  },
  {
    id: 'frontier-outlaw',
    name: 'Frontier Outlaw',
    blurb: 'A cinematic western sundown.',
    thumbnail: '/themes/03-frontier-outlaw-thumb.webp',
    backgroundImage: '/themes/03-frontier-outlaw-background.webp',
    backgroundPosition: 'center 58%',
    backgroundScale: 1.03,
    colors: {
      bg: '#120B07',
      bgSecondary: '#1C110A',
      surface: '#26170F',
      surfaceHover: '#342117',
      surfaceElevated: '#422B1D',
      surfaceStrong: '#170E09',
      border: '#68442B',
      borderHover: '#91613B',
      borderBright: '#C9823E',
      primary: '#C56E32',
      primaryHover: '#DF8848',
      primaryPressed: '#A95825',
      primarySoft: '#4A2C18',
      primaryForeground: '#FFF8EC',
      accent: '#E7B75D',
      accentSecondary: '#B67C45',
      text: '#FFF7EB',
      textSecondary: '#D3BCA1',
      textMuted: '#8F7966',
      success: '#53C982',
      warning: '#EBC45F',
      danger: '#D85847',
      offline: '#776A60',
      glow: 'rgba(197,110,50,.28)',
      glowStrong: 'rgba(223,136,72,.45)',
      glass: 'rgba(27,16,10,.84)'
    },
    effects: {
      panelBlur: '14px',
      panelSaturation: '112%',
      panelOpacity: 0.84,
      wallpaperBrightness: 0.74,
      wallpaperSaturation: 1.02,
      wallpaperOverlay: 'rgba(19,10,5,.22)',
      ambientParticleType: 'dust',
      ambientParticleAmount: 14,
      ambientParticleSpeed: 0.4,
      ambientGlowStrength: 0.8,
      bloom: 'radial-gradient(55% 40% at 50% 62%, rgba(255,150,70,.14), transparent 70%)'
    },
    surfaces: {
      panel: 'rgba(23,16,11,.88)',
      row: 'rgba(30,21,15,.86)',
      card: 'rgba(25,17,12,.90)',
      sidebar: 'rgba(29,21,18,.86)',
      nav: 'rgba(23,14,10,.80)',
      input: 'rgba(25,17,14,.90)',
      selected: 'rgba(70,42,19,.72)'
    },
    style: {
      radius: 0.5,
      blur: '0px',
      modalOpacity: 0.96,
      edge: 'rivet',
      button: 'leather',
      mark: 'brass',
      frame: 'plank',
      display: 'slab',
      line: 0.74    }
  },
  {
    id: 'lavender-nebula',
    name: 'Lavender Nebula',
    blurb: 'Purple cosmic fantasy, far from home.',
    thumbnail: '/themes/04-lavender-nebula-thumb.webp',
    backgroundImage: '/themes/04-lavender-nebula-background.webp',
    backgroundPosition: 'center center',
    backgroundScale: 1.02,
    colors: {
      bg: '#0E0D21',
      bgSecondary: '#17152F',
      surface: '#211E40',
      surfaceHover: '#2E2954',
      surfaceElevated: '#393264',
      surfaceStrong: '#15132B',
      border: '#575081',
      borderHover: '#7566AA',
      borderBright: '#A87AFF',
      primary: '#9567F0',
      primaryHover: '#B087FF',
      primaryPressed: '#7850CC',
      primarySoft: '#3B2A66',
      primaryForeground: '#FFFFFF',
      accent: '#D1A5FF',
      accentSecondary: '#8E8BFF',
      text: '#FAF7FF',
      textSecondary: '#C4BADF',
      textMuted: '#81789D',
      success: '#42DB9A',
      warning: '#FFD166',
      danger: '#FF647B',
      offline: '#77738B',
      glow: 'rgba(149,103,240,.34)',
      glowStrong: 'rgba(176,135,255,.56)',
      glass: 'rgba(19,17,43,.79)'
    },
    effects: {
      panelBlur: '20px',
      panelSaturation: '127%',
      panelOpacity: 0.79,
      wallpaperBrightness: 0.80,
      wallpaperSaturation: 1.08,
      wallpaperOverlay: 'rgba(12,8,27,.22)',
      ambientParticleType: 'star',
      ambientParticleAmount: 16,
      ambientParticleSpeed: 0.2,
      ambientGlowStrength: 0.95,
      bloom: 'radial-gradient(65% 50% at 50% 30%, rgba(170,140,255,.18), transparent 72%)'
    },
    surfaces: {
      panel: 'rgba(48,44,84,.62)',
      row: 'rgba(40,36,72,.72)',
      card: 'rgba(52,48,92,.62)',
      sidebar: 'rgba(17,17,31,.86)',
      nav: 'rgba(19,20,37,.58)',
      input: 'rgba(33,30,58,.74)',
      selected: 'rgba(60,44,104,.72)'
    },
    style: {
      radius: 0.96,
      blur: '16px',
      modalOpacity: 0.9,
      edge: 'halo',
      button: 'gradient',
      mark: 'halo',
      frame: 'hair',
      display: 'app',
      line: 0.5    }
  },
  {
    id: 'pixel-realm',
    name: 'Pixel Realm',
    blurb: 'A smooth green pixel fantasy.',
    thumbnail: '/themes/05-pixel-realm-thumb.webp',
    backgroundImage: '/themes/05-pixel-realm-background.webp',
    backgroundPosition: 'center center',
    backgroundScale: 1.02,
    colors: {
      bg: '#06110C',
      bgSecondary: '#091B12',
      surface: '#0D2718',
      surfaceHover: '#143720',
      surfaceElevated: '#1A472A',
      surfaceStrong: '#08160E',
      border: '#2D633B',
      borderHover: '#418B51',
      borderBright: '#64D881',
      primary: '#49C96D',
      primaryHover: '#68DD89',
      primaryPressed: '#36AA56',
      primarySoft: '#19482A',
      primaryForeground: '#06120A',
      accent: '#A7EB68',
      accentSecondary: '#5FD6B0',
      text: '#F3FFF5',
      textSecondary: '#B5D0BA',
      textMuted: '#708976',
      success: '#4AD878',
      warning: '#F1CA58',
      danger: '#F05D65',
      offline: '#68796D',
      glow: 'rgba(73,201,109,.25)',
      glowStrong: 'rgba(100,216,129,.43)',
      glass: 'rgba(6,22,13,.84)'
    },
    effects: {
      panelBlur: '11px',
      panelSaturation: '112%',
      panelOpacity: 0.84,
      wallpaperBrightness: 0.82,
      wallpaperSaturation: 1.02,
      wallpaperOverlay: 'rgba(4,12,7,.16)',
      ambientParticleType: 'pixel',
      ambientParticleAmount: 13,
      ambientParticleSpeed: 0.45,
      ambientGlowStrength: 0.85,
      bloom: 'radial-gradient(60% 45% at 50% 25%, rgba(110,235,150,.14), transparent 70%)'
    },
    surfaces: {
      panel: 'rgba(13,26,25,.88)',
      row: 'rgba(13,25,26,.92)',
      card: 'rgba(10,24,23,.90)',
      sidebar: 'rgba(10,18,19,.92)',
      nav: 'rgba(11,22,22,.72)',
      input: 'rgba(16,25,29,.92)',
      selected: 'rgba(22,60,42,.82)'
    },
    style: {
      radius: 0.56,
      blur: '0px',
      modalOpacity: 0.94,
      edge: 'pixel',
      button: 'block',
      mark: 'square',
      frame: 'step',
      display: 'app',
      line: 0.64    }
  },
  {
    id: 'dream-portal',
    name: 'Dream Portal',
    blurb: 'A bright floating dream world.',
    thumbnail: '/themes/06-dream-portal-thumb.webp',
    backgroundImage: '/themes/06-dream-portal-background.webp',
    backgroundPosition: 'center center',
    backgroundScale: 1.02,
    colors: {
      bg: '#12142A',
      bgSecondary: '#1B1D3D',
      surface: '#282B52',
      surfaceHover: '#343865',
      surfaceElevated: '#404578',
      surfaceStrong: '#181A36',
      border: '#696EA7',
      borderHover: '#8D8FD0',
      borderBright: '#B9A8FF',
      primary: '#8F7CFF',
      primaryHover: '#AA9BFF',
      primaryPressed: '#7564DC',
      primarySoft: '#47447A',
      primaryForeground: '#FFFFFF',
      accent: '#FFA2DF',
      accentSecondary: '#72E3F6',
      text: '#FFFBFF',
      textSecondary: '#D4D2EE',
      textMuted: '#9192B4',
      success: '#4AD89C',
      warning: '#F5D36C',
      danger: '#F16F89',
      offline: '#86879E',
      glow: 'rgba(143,124,255,.31)',
      glowStrong: 'rgba(255,162,223,.44)',
      glass: 'rgba(32,34,71,.70)'
    },
    effects: {
      panelBlur: '24px',
      panelSaturation: '132%',
      panelOpacity: 0.7,
      wallpaperBrightness: 0.90,
      wallpaperSaturation: 1.02,
      wallpaperOverlay: 'rgba(24,22,55,.12)',
      ambientParticleType: 'crystal',
      ambientParticleAmount: 14,
      ambientParticleSpeed: 0.32,
      ambientGlowStrength: 1,
      bloom: 'radial-gradient(42% 34% at 50% 44%, rgba(190,165,255,.22), transparent 68%)',
      bloomPulse: true
    },
    surfaces: {
      panel: 'rgba(72,74,126,.66)',
      row: 'rgba(51,58,104,.62)',
      card: 'rgba(78,78,136,.64)',
      sidebar: 'rgba(28,26,62,.70)',
      nav: 'rgba(48,46,96,.44)',
      input: 'rgba(53,59,109,.80)',
      selected: 'rgba(96,88,168,.62)'
    },
    style: {
      radius: 0.95,
      blur: '18px',
      modalOpacity: 0.88,
      edge: 'crystal',
      button: 'gradient',
      mark: 'halo',
      frame: 'hair',
      display: 'app',
      line: 0.54    }
  },
  {
    id: 'void-forge',
    name: 'Void Forge',
    blurb: 'A black and red volcanic forge.',
    thumbnail: '/themes/07-void-forge-thumb.webp',
    backgroundImage: '/themes/07-void-forge-background.webp',
    backgroundPosition: 'center center',
    backgroundScale: 1.02,
    colors: {
      bg: '#090708',
      bgSecondary: '#140A08',
      surface: '#1E100D',
      surfaceHover: '#2A1611',
      surfaceElevated: '#381E16',
      surfaceStrong: '#110807',
      border: '#63291E',
      borderHover: '#973824',
      borderBright: '#FF5129',
      primary: '#FF4B25',
      primaryHover: '#FF7042',
      primaryPressed: '#D8381B',
      primarySoft: '#52170D',
      primaryForeground: '#FFF7F3',
      accent: '#FF9B3C',
      accentSecondary: '#D83A32',
      text: '#FFF6F2',
      textSecondary: '#D3B2A8',
      textMuted: '#896F68',
      success: '#45D78A',
      warning: '#FFC457',
      danger: '#FF403E',
      offline: '#756764',
      glow: 'rgba(255,75,37,.34)',
      glowStrong: 'rgba(255,112,66,.52)',
      glass: 'rgba(17,7,6,.84)'
    },
    effects: {
      panelBlur: '16px',
      panelSaturation: '120%',
      panelOpacity: 0.84,
      wallpaperBrightness: 0.72,
      wallpaperSaturation: 1.08,
      wallpaperOverlay: 'rgba(10,3,2,.22)',
      ambientParticleType: 'ember',
      ambientParticleAmount: 16,
      ambientParticleSpeed: 0.5,
      ambientGlowStrength: 1,
      bloom: 'radial-gradient(60% 42% at 50% 78%, rgba(255,70,40,.20), transparent 70%)',
      bloomPulse: true
    },
    surfaces: {
      panel: 'rgba(8,9,10,.90)',
      row: 'rgba(14,12,13,.92)',
      card: 'rgba(12,10,11,.92)',
      sidebar: 'rgba(10,8,9,.88)',
      nav: 'rgba(10,7,8,.76)',
      input: 'rgba(6,8,9,.90)',
      selected: 'rgba(40,16,14,.86)'
    },
    style: {
      radius: 0.64,
      blur: '0px',
      modalOpacity: 0.95,
      edge: 'lava',
      button: 'forge',
      mark: 'lava',
      frame: 'forged',
      display: 'app',
      line: 0.6    }
  },
  {
    id: 'matrix-lime',
    name: 'Matrix Lime',
    blurb: 'An acid-green future city.',
    thumbnail: '/themes/08-matrix-lime-thumb.webp',
    backgroundImage: '/themes/08-matrix-lime-background.webp',
    backgroundPosition: 'center center',
    backgroundScale: 1.02,
    colors: {
      bg: '#030A06',
      bgSecondary: '#06140B',
      surface: '#091E11',
      surfaceHover: '#102B18',
      surfaceElevated: '#153820',
      surfaceStrong: '#050F08',
      border: '#305D2D',
      borderHover: '#4B8733',
      borderBright: '#9BFF28',
      primary: '#98F82A',
      primaryHover: '#B6FF54',
      primaryPressed: '#78D315',
      primarySoft: '#274A13',
      primaryForeground: '#071005',
      accent: '#D2FF61',
      accentSecondary: '#51D96F',
      text: '#F7FFF2',
      textSecondary: '#B7CDAE',
      textMuted: '#72896A',
      success: '#52E477',
      warning: '#F4D951',
      danger: '#FF5967',
      offline: '#687666',
      glow: 'rgba(152,248,42,.31)',
      glowStrong: 'rgba(182,255,84,.52)',
      glass: 'rgba(3,14,7,.83)'
    },
    effects: {
      panelBlur: '15px',
      panelSaturation: '124%',
      panelOpacity: 0.83,
      wallpaperBrightness: 0.70,
      wallpaperSaturation: 1.08,
      wallpaperOverlay: 'rgba(2,8,4,.24)',
      ambientParticleType: 'digital',
      ambientParticleAmount: 12,
      ambientParticleSpeed: 0.7,
      ambientGlowStrength: 0.9,
      bloom: 'radial-gradient(60% 45% at 50% 70%, rgba(150,255,70,.14), transparent 72%)'
    },
    surfaces: {
      panel: 'rgba(14,24,22,.86)',
      row: 'rgba(16,25,23,.90)',
      card: 'rgba(12,22,17,.88)',
      sidebar: 'rgba(8,18,12,.88)',
      nav: 'rgba(10,20,16,.70)',
      input: 'rgba(14,22,21,.90)',
      selected: 'rgba(24,48,18,.82)'
    },
    style: {
      radius: 0.47,
      blur: '4px',
      modalOpacity: 0.95,
      edge: 'scan',
      button: 'tech',
      mark: 'line',
      frame: 'edge',
      display: 'tech',
      line: 0.62    }
  },
  {
    id: 'jungle-temple',
    name: 'Jungle Temple',
    blurb: 'Ancient green and gold adventure.',
    thumbnail: '/themes/09-jungle-temple-thumb.webp',
    backgroundImage: '/themes/09-jungle-temple-background.webp',
    backgroundPosition: 'center 48%',
    backgroundScale: 1.02,
    colors: {
      bg: '#07110C',
      bgSecondary: '#0B1B12',
      surface: '#10261A',
      surfaceHover: '#173421',
      surfaceElevated: '#1D422A',
      surfaceStrong: '#09170F',
      border: '#315D3B',
      borderHover: '#478052',
      borderBright: '#55D879',
      primary: '#48CF72',
      primaryHover: '#6DE28E',
      primaryPressed: '#35AE5A',
      primarySoft: '#19482A',
      primaryForeground: '#06120A',
      accent: '#F2C86A',
      accentSecondary: '#7DDDC2',
      text: '#F5FFF7',
      textSecondary: '#B7D0BC',
      textMuted: '#708975',
      success: '#47D879',
      warning: '#F2C45D',
      danger: '#F55C63',
      offline: '#68786B',
      glow: 'rgba(72,207,114,.27)',
      glowStrong: 'rgba(109,226,142,.45)',
      glass: 'rgba(7,22,13,.81)'
    },
    effects: {
      panelBlur: '18px',
      panelSaturation: '121%',
      panelOpacity: 0.81,
      wallpaperBrightness: 0.75,
      wallpaperSaturation: 1.03,
      wallpaperOverlay: 'rgba(4,13,7,.23)',
      ambientParticleType: 'firefly',
      ambientParticleAmount: 12,
      ambientParticleSpeed: 0.24,
      ambientGlowStrength: 0.85,
      bloom: 'radial-gradient(60% 45% at 50% 35%, rgba(255,205,110,.14), transparent 72%)'
    },
    surfaces: {
      panel: 'rgba(8,20,15,.88)',
      row: 'rgba(12,24,19,.90)',
      card: 'rgba(10,22,16,.90)',
      sidebar: 'rgba(10,17,13,.88)',
      nav: 'rgba(9,18,14,.72)',
      input: 'rgba(10,20,17,.90)',
      selected: 'rgba(10,50,32,.80)'
    },
    style: {
      radius: 0.64,
      blur: '0px',
      modalOpacity: 0.94,
      edge: 'gold',
      button: 'stone',
      mark: 'goldline',
      frame: 'stone',
      display: 'serif',
      line: 0.68    }
  },
  {
    id: 'ruby-midnight',
    name: 'Ruby Midnight',
    blurb: 'A dark crimson cosmic world.',
    thumbnail: '/themes/10-ruby-midnight-thumb.webp',
    backgroundImage: '/themes/10-ruby-midnight-background.webp',
    backgroundPosition: 'center center',
    backgroundScale: 1.02,
    colors: {
      bg: '#09070B',
      bgSecondary: '#15090E',
      surface: '#201016',
      surfaceHover: '#30161E',
      surfaceElevated: '#3B1B24',
      surfaceStrong: '#12080C',
      border: '#612535',
      borderHover: '#923347',
      borderBright: '#F0435B',
      primary: '#EE334E',
      primaryHover: '#FF536A',
      primaryPressed: '#C92840',
      primarySoft: '#50131F',
      primaryForeground: '#FFFFFF',
      accent: '#FF7586',
      accentSecondary: '#C83B68',
      text: '#FFF5F6',
      textSecondary: '#CCB0B5',
      textMuted: '#866A70',
      success: '#3BD78A',
      warning: '#FFC34D',
      danger: '#FF334E',
      offline: '#786A6D',
      glow: 'rgba(238,51,78,.34)',
      glowStrong: 'rgba(255,83,106,.54)',
      glass: 'rgba(17,7,12,.83)'
    },
    effects: {
      panelBlur: '18px',
      panelSaturation: '125%',
      panelOpacity: 0.83,
      wallpaperBrightness: 0.72,
      wallpaperSaturation: 1.08,
      wallpaperOverlay: 'rgba(10,3,7,.25)',
      ambientParticleType: 'cosmic',
      ambientParticleAmount: 12,
      ambientParticleSpeed: 0.22,
      ambientGlowStrength: 0.95,
      bloom: 'radial-gradient(60% 45% at 50% 24%, rgba(255,70,105,.16), transparent 72%)'
    },
    surfaces: {
      panel: 'rgba(22,14,17,.84)',
      row: 'rgba(24,17,20,.88)',
      card: 'rgba(18,12,15,.86)',
      sidebar: 'rgba(14,11,13,.86)',
      nav: 'rgba(12,7,10,.62)',
      input: 'rgba(22,13,16,.86)',
      selected: 'rgba(48,16,26,.82)'
    },
    style: {
      radius: 0.79,
      blur: '6px',
      modalOpacity: 0.94,
      edge: 'ruby',
      button: 'glass',
      mark: 'glow',
      frame: 'hair',
      display: 'app',
      line: 0.54    }
  }
];

const BY_ID = new Map(SCENES.map((s) => [s.id, s]));

/** Whether a saved theme id is one of the scenes. */
export function isScene(id: string | null | undefined): id is SceneId {
  return !!id && BY_ID.has(id as SceneId);
}

/** The scene with this id, or null for the flat themes. */
export function scene(id: string | null | undefined): Scene | null {
  return (id && BY_ID.get(id as SceneId)) || null;
}
